import { Box, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { Skeleton } from '@/components';
import { ComplianceTypes, Routes } from '@/constants';
import { useEpisodeCategories } from '@/hooks';
import {
  selectReportDateRange,
  selectUserCompliance,
  useAppSelector,
  useFetchTopEpisodesReportQuery,
} from '@/store';
import { ComplianceType, EpisodeCard } from '@/types';
import { createRouteWithParams } from '@/utils';

import { styles } from '../../styles';
import { EmptyChartView, EmptyViewImageKeys } from '../EmptyChartView';
import { TileHeader } from '../TileHeader';

export const TopEpisodes = () => {
  const dateRange = useAppSelector(selectReportDateRange);
  const userCompliance = useAppSelector(selectUserCompliance);

  const { data, isFetching } = useFetchTopEpisodesReportQuery(
    {
      dateRange,
      userCompliance,
    },
    {
      skip: !dateRange.fromDate || !dateRange.toDate,
    },
  );

  const isEmpty = useMemo(() => {
    if (!data) return false;
    return data.length === 0;
  }, [data]);

  return (
    <Box sx={[styles.tile, styles.tileWithMinHeight, styles.column]}>
      <TileHeader
        title="Top Episodes"
        description="Highlights your team's most listened to episodes in the selected time period. Compliance is not included in this metric. Each team member is counted once per episode."
      />
      {isFetching && (
        <Box>
          {Array(5)
            .fill(null)
            .map((_, idx: number) => (
              <Box key={idx} sx={styles.listItem}>
                <Skeleton />
              </Box>
            ))}
        </Box>
      )}
      {!isFetching && isEmpty && (
        <EmptyChartView
          title={EMPTY_CHART_VIEW_TITLE}
          subtitle={EMPTY_CHART_VIEW_SUBTITLE}
          imageKey={EmptyViewImageKeys.headphones}
        />
      )}
      {!isFetching && !isEmpty && data && (
        <>
          <Box sx={{ flexGrow: 1 }}>
            {data
              .slice(0, 10)
              .map(
                ({ episode, engagementCount }, idx: number) =>
                  episode && (
                    <EpisodeListItem
                      key={episode.episodeId}
                      episode={episode}
                      memberListenStartedCount={engagementCount}
                      index={idx}
                      userCompliance={userCompliance}
                    />
                  ),
              )}
          </Box>
        </>
      )}
    </Box>
  );
};

const EpisodeListItem = ({
  episode,
  memberListenStartedCount,
  index,
  userCompliance,
}: {
  episode: EpisodeCard;
  memberListenStartedCount: number;
  index: number;
  userCompliance: ComplianceType;
}) => {
  const navigate = useNavigate();
  const categories = useEpisodeCategories(episode);

  const onClick = () => {
    navigate({
      pathname: createRouteWithParams(Routes.EPISODE, {
        episodeId: episode.episodeId,
      }),
    });
  };

  return (
    <Box
      key={episode.episodeId}
      sx={[styles.listItem, { cursor: 'pointer' }]}
      onClick={onClick}>
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
        <Box>
          <Typography variant="body2SemiBold" sx={{ width: 24 }}>
            {index + 1}.
          </Typography>
        </Box>
        <Box
          component="img"
          src={episode.cover}
          sx={[
            styles.listItemImageSize,
            {
              objectFit: 'cover',
              borderRadius: 2,
            },
          ]}
        />
        <Box>
          <Typography
            variant="body3SemiBold"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              mb: 0.5,
            }}>
            {episode.name}
          </Typography>
          <Typography
            variant="caption1"
            sx={{
              color: 'text.light',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              WebkitLineClamp: 1,
              WebkitBoxOrient: 'vertical',
              display: { xs: 'none', md: '-webkit-box' },
            }}>
            {userCompliance === ComplianceTypes.CPE
              ? episode.fieldOfStudy
              : categories}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ pl: 4 }}>
        <Typography variant="body2SemiBold" sx={{ color: 'secondary.main' }}>
          {memberListenStartedCount.toLocaleString()}
        </Typography>
      </Box>
    </Box>
  );
};

const EMPTY_CHART_VIEW_TITLE = 'No episodes played during this period';
const EMPTY_CHART_VIEW_SUBTITLE =
  "Top episodes will appear here as soon as there's activity.";
